import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../redux";
import { fetchPreferences, updatePreference, resetPreferences } from "../redux/reducers/notificationPreferenceSlice";
import { View, Text, Button, Card, CardHeader, CardTitle, CardDescription, CardContent, Spinner } from "../components/base";
import { DropdownField, CheckboxField } from "../components/base";
import { PageLayout } from "../components/PageLayout";
import { useApiRequest } from "../hooks/useApiRequest";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { API_URL } from "../config";
import { makeRequest } from "../utils/request";

// Active categories - add more as they become functional
const CATEGORIES = [
  { value: "test", label: "Tests", description: "Test completion and failures" },
  { value: "billing", label: "Billing", description: "Billing and payment updates" },
  // Future categories (uncomment when ready):
  // { value: "account", label: "Account", description: "Account-related notifications (invites, membership changes)" },
];

interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
}

interface SlackIntegration {
  id: string;
  publicId: string;
  organizationId: string;
  channelId?: string;
  accessToken?: string;
  teamId?: string;
  isActive: boolean;
}

export const NotificationPreferencesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { preferences, isLoading } = useAppSelector((state) => state.notificationPreferences);
  const user = useAppSelector((state) => state.session.user);
  const { organizationId } = useEffectiveOrganization();
  const [changes, setChanges] = useState<Record<string, any>>({});
  const saveRequest = useApiRequest();
  const resetRequest = useApiRequest();

  // Slack integration state
  const [slackIntegration, setSlackIntegration] = useState<SlackIntegration | null>(null);
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string>("");
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [isUpdatingChannel, setIsUpdatingChannel] = useState(false);
  const integrationRequest = useApiRequest<{ data: SlackIntegration | null }>();
  const channelsRequest = useApiRequest<{ data: SlackChannel[] }>();

  // Use refs to store the latest request objects to avoid dependency issues
  const integrationRequestRef = useRef(integrationRequest);
  const channelsRequestRef = useRef(channelsRequest);

  useEffect(() => {
    integrationRequestRef.current = integrationRequest;
    channelsRequestRef.current = channelsRequest;
  }, [integrationRequest, channelsRequest]);

  useEffect(() => {
    dispatch(fetchPreferences());
  }, [dispatch]);

  // Fetch channels from Slack
  const fetchChannels = useCallback(async () => {
    if (!organizationId) return;

    setIsLoadingChannels(true);
    try {
      const response = await channelsRequestRef.current.fetch(`/v1/organization-slack/channels`);

      if (response?.data) {
        const apiResponse = response.data as { data: SlackChannel[] };
        if (apiResponse.data) {
          setSlackChannels(apiResponse.data);
        }
      }
    } catch (error) {
      // Error is handled by useApiRequest hook
    } finally {
      setIsLoadingChannels(false);
    }
  }, [organizationId]);

  // Fetch Slack integration
  const fetchSlackIntegration = useCallback(async () => {
    if (!organizationId) return;

    try {
      const response = await integrationRequestRef.current.fetch(`/v1/organization-slack`);

      if (response?.data) {
        // Check if response.data is already the integration object or wrapped
        let integration: SlackIntegration | null = null;

        if (response.data && typeof response.data === "object") {
          // Check if it's wrapped in a 'data' property
          if ("data" in response.data && response.data.data !== undefined) {
            const wrapped = response.data as { data: SlackIntegration | null };
            integration = wrapped.data;
          } else {
            // Check if it looks like an integration object
            const data = response.data as any;
            if (data && (data.publicId || data.id) && typeof data.isActive === "boolean") {
              integration = data as SlackIntegration;
            }
          }
        }

        if (integration && integration.isActive) {
          setSlackIntegration(integration);
          if (integration.channelId) {
            setSelectedChannelId(integration.channelId);
          } else {
            setSelectedChannelId("");
          }
          // Always fetch channels if integration exists and has access token
          if (integration.accessToken) {
            fetchChannels().catch(() => {
              // Error is handled by useApiRequest hook
            });
          }
        } else {
          setSlackIntegration(null);
          setSlackChannels([]);
          setSelectedChannelId("");
        }
      } else if (response?.error) {
        // If 404, no integration exists
        if (response.error.message?.includes("404") || response.error.message?.includes("not found")) {
          setSlackIntegration(null);
          setSlackChannels([]);
          setSelectedChannelId("");
        }
      }
    } catch (error) {
      // Error is handled by useApiRequest hook
      setSlackIntegration(null);
      setSlackChannels([]);
      setSelectedChannelId("");
    }
  }, [organizationId, fetchChannels]);

  // Check for OAuth success/error messages
  useEffect(() => {
    const success = searchParams.get("success");
    const error = searchParams.get("error");

    if (success === "slack_connected") {
      // Refresh integration after successful connection
      if (organizationId) {
        // Small delay to ensure backend has processed the integration
        setTimeout(() => {
          fetchSlackIntegration();
        }, 1000);
      }
      // Remove query param after a delay to allow state to update
      setTimeout(() => {
        navigate("/notification-preferences", { replace: true });
      }, 100);
    } else if (error) {
      // OAuth error - user will see it in the UI if needed
      // Remove query param
      navigate("/notification-preferences", { replace: true });
    }
  }, [searchParams, organizationId, fetchSlackIntegration, navigate]);

  // Update selected channel
  const handleChannelChange = async (channelId: string) => {
    if (!slackIntegration || !organizationId) return;

    // Don't update if it's the same channel
    if (channelId === selectedChannelId) return;

    setSelectedChannelId(channelId);
    setIsUpdatingChannel(true);

    try {
      await makeRequest(`/v1/organization-slack/${slackIntegration.publicId}/channel`, {
        method: "PUT",
        body: { channelId },
      });
      // Refresh integration to get updated channelId
      await fetchSlackIntegration();
    } catch (error) {
      // Revert selection on error
      setSelectedChannelId(slackIntegration.channelId || "");
      alert("Failed to update channel. Please try again.");
    } finally {
      setIsUpdatingChannel(false);
    }
  };

  // Disconnect Slack integration
  const handleDisconnect = async () => {
    if (!organizationId) return;

    if (!window.confirm("Are you sure you want to disconnect Slack? You will no longer receive notifications in Slack.")) {
      return;
    }

    try {
      await makeRequest(`/v1/organization-slack/disconnect`, {
        method: "POST",
      });
      // Clear integration state
      setSlackIntegration(null);
      setSlackChannels([]);
      setSelectedChannelId("");
    } catch (error) {
      alert("Failed to disconnect Slack. Please try again.");
    }
  };

  // Fetch integration on mount and when organizationId changes
  useEffect(() => {
    if (organizationId) {
      fetchSlackIntegration();
    }
  }, [organizationId, fetchSlackIntegration]);

  const handleToggleChannel = (category: string, channel: "inApp" | "email" | "slack", value: boolean) => {
    setChanges((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        channels: {
          ...(prev[category]?.channels || preferences[category]?.channels || {}),
          [channel]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      for (const [category, updates] of Object.entries(changes)) {
        await dispatch(updatePreference({ category, updates })).unwrap();
      }
      setChanges({});
      // Show success message
    } catch (error) {
      // Error is handled by Redux
    }
  };

  const handleReset = async () => {
    try {
      await dispatch(resetPreferences()).unwrap();
      await dispatch(fetchPreferences());
      setChanges({});
      // Show success message
    } catch (error) {
      // Error is handled by Redux
    }
  };

  const hasChanges = Object.keys(changes).length > 0;

  // Helper to get preference value for a category/channel
  const getChannelValue = (categoryValue: string, channel: "inApp" | "email" | "slack") => {
    const originalPreference = preferences[categoryValue];
    const changedPreference = changes[categoryValue];

    if (changedPreference?.channels?.[channel] !== undefined) {
      return changedPreference.channels[channel];
    }
    return originalPreference?.channels?.[channel] ?? (channel === "slack" ? false : true);
  };

  // Master toggle handlers
  const handleToggleAllForChannel = (channel: "inApp" | "email" | "slack", value: boolean) => {
    const newChanges = { ...changes };
    CATEGORIES.forEach((category) => {
      newChanges[category.value] = {
        ...newChanges[category.value],
        channels: {
          ...(newChanges[category.value]?.channels || preferences[category.value]?.channels || {}),
          [channel]: value,
        },
      };
    });
    setChanges(newChanges);
  };

  // Check if all categories have a channel enabled
  const isAllEnabledForChannel = (channel: "inApp" | "email" | "slack") => {
    return CATEGORIES.every((category) => getChannelValue(category.value, channel));
  };

  // Check if some (but not all) categories have a channel enabled
  const isSomeEnabledForChannel = (channel: "inApp" | "email" | "slack") => {
    const enabled = CATEGORIES.filter((category) => getChannelValue(category.value, channel));
    return enabled.length > 0 && enabled.length < CATEGORIES.length;
  };

  return (
    <PageLayout
      title="Notification Preferences"
      description="Manage how and where you receive notifications"
      backTo="/profile"
      backLabel="Back to Profile"
    >
      {isLoading ? (
        <View className="flex items-center justify-center py-12">
          <Text>Loading...</Text>
        </View>
      ) : (
        <View className="space-y-6 mt-6">
          {/* Slack Integration Section */}
          <Card>
            <CardHeader>
              <View className="flex items-center justify-between">
                <View>
                  <CardTitle>Slack Integration</CardTitle>
                  <CardDescription className="mt-1">
                    {slackIntegration?.isActive ? "Connected to Slack workspace" : "Connect your Slack workspace to receive notifications"}
                  </CardDescription>
                </View>
                {!slackIntegration?.isActive ? (
                  <Button
                    type="button"
                    variant="brand"
                    onClick={() => {
                      if (!organizationId || !user?.id) {
                        return;
                      }

                      const clientId = "7146691776500.9942204701057";
                      const scope = "chat:write,channels:read,groups:read,channels:join,groups:write";
                      const redirectUri = `${API_URL}/slack/oauth`;
                      const state = `${organizationId}:${user.id}`;
                      const slackOAuthUrl = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${scope}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
                      window.location.href = slackOAuthUrl;
                    }}
                  >
                    Connect Slack
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleDisconnect}
                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Disconnect
                  </Button>
                )}
              </View>
            </CardHeader>
            {slackIntegration?.isActive && (
              <CardContent className="space-y-4">
                <View className="space-y-2">
                  <Text className="text-sm font-medium text-gray-900 dark:text-white">Notification Channel</Text>
                  {isLoadingChannels ? (
                    <View className="flex items-center gap-2 py-2">
                      <Spinner
                        size={16}
                        variant="brand"
                      />
                      <Text className="text-sm text-gray-500 dark:text-gray-400">Loading channels...</Text>
                    </View>
                  ) : slackChannels.length > 0 ? (
                    <DropdownField
                      name="slack-channel"
                      value={selectedChannelId}
                      onChange={handleChannelChange}
                      options={slackChannels.map((channel) => ({
                        value: channel.id,
                        label: `${channel.is_private ? "🔒 " : "#"}${channel.name}`,
                        displayLabel: `${channel.is_private ? "🔒 " : "#"}${channel.name}`,
                      }))}
                      placeholder="Select a channel"
                      readOnly={isUpdatingChannel}
                    />
                  ) : (
                    <View className="space-y-2">
                      <Text className="text-sm text-gray-500 dark:text-gray-400">No channels available. Click below to load channels.</Text>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={fetchChannels}
                      >
                        Load Channels
                      </Button>
                    </View>
                  )}
                  <Text className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedChannelId ? "Notifications will be sent to the selected channel" : "Select a channel to receive Slack notifications"}
                  </Text>
                </View>
              </CardContent>
            )}
          </Card>

          {/* Notification Preferences Matrix */}
          <View className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <View className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-6">
                      <Text className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Category</Text>
                    </th>
                    <th className="text-center py-3 px-4 min-w-[100px] align-middle">
                      <Text className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 block mb-1">In-App</Text>
                      <CheckboxField
                        value={isAllEnabledForChannel("inApp")}
                        onChange={(checked) => handleToggleAllForChannel("inApp", checked)}
                        indeterminate={isSomeEnabledForChannel("inApp")}
                        className="mx-auto"
                      />
                    </th>
                    <th className="text-center py-3 px-4 min-w-[100px] align-middle">
                      <Text className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 block mb-1">Email</Text>
                      <CheckboxField
                        value={isAllEnabledForChannel("email")}
                        onChange={(checked) => handleToggleAllForChannel("email", checked)}
                        indeterminate={isSomeEnabledForChannel("email")}
                        className="mx-auto"
                      />
                    </th>
                    <th className={`text-center py-3 px-4 min-w-[140px] align-middle ${!slackIntegration?.isActive ? "opacity-40" : ""}`}>
                      <Text className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 block mb-1">
                        Slack{!slackIntegration?.isActive && " (Not connected)"}
                      </Text>
                      <CheckboxField
                        value={isAllEnabledForChannel("slack")}
                        onChange={(checked) => handleToggleAllForChannel("slack", checked)}
                        indeterminate={isSomeEnabledForChannel("slack")}
                        isDisabled={!slackIntegration?.isActive}
                        className="mx-auto"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {CATEGORIES.map((category) => (
                    <tr
                      key={category.value}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <View>
                          <Text className="text-sm font-medium text-gray-900 dark:text-white mr-1">{category.label}</Text>
                          <Text className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{category.description}</Text>
                        </View>
                      </td>
                      <td className="text-center py-4 px-4 align-middle">
                        <CheckboxField
                          value={getChannelValue(category.value, "inApp")}
                          onChange={(checked) => handleToggleChannel(category.value, "inApp", checked)}
                          className="mx-auto"
                        />
                      </td>
                      <td className="text-center py-4 px-4 align-middle">
                        <CheckboxField
                          value={getChannelValue(category.value, "email")}
                          onChange={(checked) => handleToggleChannel(category.value, "email", checked)}
                          className="mx-auto"
                        />
                      </td>
                      <td className={`text-center py-4 px-4 align-middle ${!slackIntegration?.isActive ? "opacity-40" : ""}`}>
                        <CheckboxField
                          value={getChannelValue(category.value, "slack")}
                          onChange={(checked) => handleToggleChannel(category.value, "slack", checked)}
                          isDisabled={!slackIntegration?.isActive}
                          className="mx-auto"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </View>
          </View>

          {/* Actions */}
          <View className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={handleReset}
              disabled={resetRequest.isLoading}
            >
              Reset to Defaults
            </Button>
            <Button
              type="button"
              variant="brand"
              onClick={handleSave}
              disabled={!hasChanges || saveRequest.isLoading}
            >
              {saveRequest.isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </View>
        </View>
      )}
    </PageLayout>
  );
};
